import { Link } from 'react-router-dom';

export interface AreaData {
  id: string;
  name: string;
  region: string;
  tagline: string;
  intro: string;
  localContext: string;
  services: string[];
  stats: { num: string; label: string }[];
  image: string;
  flip?: boolean;
}

interface AreaCardProps {
  area: AreaData;
  index: number;
}

export default function AreaCard({ area, index }: AreaCardProps) {
  const flip = index % 2 !== 0;

  return (
    <article
      id={area.id}
      className="relative border-b border-ds-border last:border-b-0 overflow-hidden"
    >
      <div className={`grid lg:grid-cols-2 min-h-[480px] ${flip ? '' : ''}`}>
        {/* Image col */}
        <div className={`relative h-64 lg:h-auto overflow-hidden ${flip ? 'lg:order-2' : 'lg:order-1'}`}>
          <img
            src={area.image}
            alt={`${area.name} ${area.region} — Darkside Digital Service Area`}
            className="w-full h-full object-cover object-top"
            loading="lazy"
            decoding="async"
            width="800"
            height="600"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-ds-bg/80 via-ds-bg/30 to-transparent lg:bg-gradient-to-r" />
          <div className="absolute inset-0 bg-gradient-to-t from-ds-bg/90 via-ds-bg/20 to-transparent" />

          {/* Area badge overlaid on image */}
          <div className="absolute bottom-6 left-6 right-6">
            <div className="inline-flex items-center gap-2 border border-ds-red/40 px-3 py-1.5 bg-ds-bg/80 backdrop-blur-sm">
              <div className="w-1.5 h-1.5 rounded-full bg-ds-red flex-shrink-0" />
              <span className="font-body text-ds-red text-xs tracking-[0.25em] uppercase">
                {area.region}
              </span>
            </div>
          </div>
        </div>

        {/* Content col */}
        <div className={`flex flex-col justify-center p-10 lg:p-14 bg-ds-bg ${flip ? 'lg:order-1' : 'lg:order-2'}`}>
          {/* Index label */}
          <span className="font-display text-6xl text-white/5 leading-none select-none absolute top-6 right-8">
            {String(index + 1).padStart(2, '0')}
          </span>

          <h2 className="font-display text-4xl lg:text-5xl text-white leading-none mb-2">
            {area.name.toUpperCase()}
          </h2>
          <p className="font-body text-ds-red text-sm italic mb-6">{area.tagline}</p>

          <div className="h-px w-12 bg-ds-red mb-6" />

          <p className="font-body text-white/60 text-sm leading-relaxed mb-4">
            {area.intro}
          </p>

          {/* Local context callout */}
          <div
            className="border-l-2 border-ds-red/50 pl-4 mb-6 py-1"
          >
            <p className="font-body text-white/75 text-xs leading-relaxed italic">
              {area.localContext}
            </p>
          </div>

          {/* Stats row */}
          <div className="flex items-center gap-6 mb-6">
            {area.stats.map((s) => (
              <div key={s.label}>
                <div className="font-display text-2xl text-ds-red leading-none">
                  {s.num}
                </div>
                <div className="font-body text-white/40 text-xs tracking-widest uppercase mt-0.5">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          {/* Services chips */}
          <div className="flex flex-wrap gap-2 mb-8">
            {area.services.map((svc) => (
              <span
                key={svc}
                className="font-body text-xs text-white/55 border border-ds-border px-3 py-1 hover:border-ds-red/40 hover:text-white transition-colors duration-200"
              >
                {svc}
              </span>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <Link
              to="/#contact"
              className="btn-red text-xs whitespace-nowrap"
            >
              Get a Free Audit for {area.name}
            </Link>
            <a
              href="tel:1300029054"
              className="font-body text-white/40 hover:text-white text-xs transition-colors duration-200 whitespace-nowrap flex items-center gap-1.5"
            >
              <div className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-phone-line text-ds-red text-xs" />
              </div>
              1300 029 054
            </a>
          </div>
        </div>
      </div>
    </article>
  );
}
